import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { IconButton, Menu, MenuItem } from '@mui/material';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import InfoMsg from '@/components/UI/InfoMsg/InfoMsg';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import { getNewStateTranslation, validateVehiclestranslations } from '@/helpers/campaignValidations';
import { CLASSIFICATION, DESCRIPTION, ERROR, HELP_AREA, SUCCESS } from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { deepClone, modulesToList } from '@/helpers/utils';
import useCheckDisabled from '@/hooks/useCheckDisabled';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useError from '@/hooks/useError';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import { setCampaignTranslations, setTranslationValidations, setTranslations } from '@/redux/Campaign/campaign.action';
import {
  getCampaignInfo, getCampaingTranslations, getTranslationValidations,
  getCampaignValidations, getOriginalValues, getArePendingVolumes,
} from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { setAreaHelp } from '@/redux/Help/help.action';
import campaignServices from '@/services/campaignServices';
import { CampaignInfo, Validations, Translations as TranslationsType, RouterParams, Plant } from '@/types';
import { mapVehiclesTranslatablePropertiesByPlant } from './translations-utils';
import TranslationsAccordion from './TranslationsAccordion/TranslationsAccordion';
import styles from './Translations.module.scss';

type TranslationsProps = {
  setCampaignTrans: (value: TranslationsType) => void;
  translations: TranslationsType;
  defaultLanguage: string;
  setTransValidations: (value: Validations) => void;
  translitonsValidations: Validations;
  setTransData: (value: TranslationsType) => void;
  campaignInfo: CampaignInfo;
  validations: Validations;
  setHelpArea: (value: string) => void;
  pendingVolumes: boolean;
};

const Translations: React.FC<TranslationsProps> = (props) => {
  const { setCampaignTrans, translations, defaultLanguage, setTransValidations,
    translitonsValidations, setTransData, campaignInfo, validations, setHelpArea, pendingVolumes } = props;
  const translationLang = defaultLanguage.toUpperCase() === 'EN' ? 'DE' : 'EN';
  const { handleCriticalError } = useError();
  const { t } = useTranslation();
  const { id } = useParams<RouterParams>();
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();
  const [isLoading, setIsLoading] = useState(true);
  const [isExpandedAll, setIsExpandedAll] = useState<{ button: string; action: boolean }>({ button: 'expandAll', action: false });
  const [opens, setOpens] = useState<string[]>([]);
  const [isOpenPopupAutoTrans, setOpenPopupAutoTrans] = useState<boolean>(false);
  const { isButtonDisabled } = useCRPermissionMatrix(campaignInfo?.status, campaignInfo?.authorization);
  const checkDisabled = useCheckDisabled(false, false) || isButtonDisabled(CR_PERMISSION_ACTIONS.TRANSLATIONS_TAB);
  const showSubmitAlert = (ok = true) => addToast(t(ok ? 'alert.autoTransSuccess' : 'alert.autoTransFailure'), ok ? SUCCESS : ERROR);
  const [anchorEl, setAnchorEl] = useState(null);

  const getTranslations = (fromAutoTrans: boolean = false) => {
    setIsLoading(true);

    if (!pendingVolumes) {
      campaignServices.getTranslations(id)
        .then((res) => {
          setTransData(res);

          if (fromAutoTrans) showSubmitAlert(true);
        })
        .catch(handleCriticalError)
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    getTranslations();
  }, [pendingVolumes, campaignInfo.title]); // eslint-disable-line react-hooks/exhaustive-deps

  const validateField = (label: string, value: string) => {
    const fields = ['campaignTitle', 'consultationComment', CLASSIFICATION.RELEVANCE_COMMENT, 'consequences', 'immediateMeasure',
      DESCRIPTION.INSPECTION_COMMENT, 'problemDescription', 'rootCause', DESCRIPTION.TRIGGER_COMMENT, 'comment', 'commentsApproverCauser',
      'commentsApproverInformer', 'ioIdentification'];

    let labelparam = label;

    if (fields.includes(label)) {
      if (['consultationComment', DESCRIPTION.TRIGGER_COMMENT, 'comment', CLASSIFICATION.RELEVANCE_COMMENT].includes(label)) {
        labelparam += 'Translations';
      }

      const newState = {
        ...translitonsValidations,
        [translationLang]: getNewStateTranslation(translitonsValidations?.[translationLang], label, value, labelparam),
      };
      setTransValidations(newState);
    }
  };

  const handleTranslationChange = (value: string, tab: string, label: string) => {
    const newValues = deepClone(translations);
    newValues.modules[tab].translatableProperties[label].translations[translationLang] = value;
    setCampaignTrans(newValues);

    if (label === 'campaignTitle' && campaignInfo.title[translationLang]) {
      campaignInfo.title[translationLang] = value;
    }
    validateField(label, value);
  };

  const validateVehiclesFields = (modules) => {
    const newState = {
      ...translitonsValidations,
      [translationLang]: {
        ...translitonsValidations?.[translationLang] as object,
        affectedVehicles: validateVehiclestranslations(modules, defaultLanguage, translationLang),
      },
    };
    setTransValidations(newState as Validations);
  };

  const handleVehiclesTranslationsChange = (value: string, tab: string, label: string, plantSelected: Plant, uuid: string) => {
    let newValues = deepClone(translations);

    newValues = {
      ...newValues,
      modules: {
        ...newValues.modules,
        [tab]: {
          translatablePropertiesByPlant: newValues.modules[tab].translatablePropertiesByPlant
            .map((plantsTranslations) => mapVehiclesTranslatablePropertiesByPlant(label, plantsTranslations, plantSelected,
              value, uuid, translationLang)),
        },
      },
    };

    setCampaignTrans(newValues);
    validateVehiclesFields(newValues.modules);
  };

  const handleAcceptAutoTrans = (isOnlyEmptyFields) => {
    setLoading(true);
    setOpenPopupAutoTrans(false);
    campaignServices.getAutomaticTranslations(id, isOnlyEmptyFields)
      .then(() => getTranslations(true))
      .catch(() => showSubmitAlert(false))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (Object.keys(modulesToList(translations, campaignInfo))?.length === opens.length) {
      setIsExpandedAll({ button: 'collapseAll', action: true });
    } else if (opens.length === 0 && isExpandedAll.button === 'collapseAll') {
      setIsExpandedAll({ button: 'expandAll', action: false });
    }
  }, [opens]); // eslint-disable-line react-hooks/exhaustive-deps

  const expandCollapseAll = () => {
    if (!isExpandedAll.action) { setOpens(Object.keys(modulesToList(translations, campaignInfo))); } else {
      setOpens([]);
    }
    setIsExpandedAll({ button: isExpandedAll.action ? 'expandAll' : 'collapseAll', action: !isExpandedAll.action });
  };

  const openMenu = (ev) => setAnchorEl(ev.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const selectTranslation = (selection) => {
    if (selection === 'all') {
      setOpenPopupAutoTrans(true);
      closeMenu();
    } else {
      handleAcceptAutoTrans(true);
      closeMenu();
    }
  };

  return (
    <>
      <InfoMsg msg={t('translations.translationNeeded', { lang: t(`languages.${translationLang}`) })} action="Warning" color="Yellow" />
      <AccordionPanel
        title={t('translations.title')}
        handleClick={() => setHelpArea(HELP_AREA.TRANSLATIONS)}
      >
        {isLoading || pendingVolumes
          ? (<div className={styles.Spinner}><Spinner isVisible /></div>)
          : (
            <div className={styles.Content}>
              <div className={styles.Flex}>
                <div className={styles.ButtonsContentTable}>
                  <>
                    <div>
                      <SimpleButton
                        id="expand-collapse-all"
                        color="primary"
                        buttonText={t(`translations.${isExpandedAll.button}`)}
                        handleClick={() => expandCollapseAll()}
                      />
                    </div>
                    <div className={styles.TitleContainer}>
                      <SimpleButton
                        disabled={checkDisabled}
                        color="primary"
                        buttonText={t('translations.autoTranslation')}
                        handleClick={(ev) => openMenu(ev)}
                        id="automatic-translation"
                      />
                      <IconButton size="small">
                        {Boolean(anchorEl) && <i className={`di icon-pfeil-chevron-oben ${styles.Icon} `} />}
                        {!anchorEl && <i className={`di icon-pfeil-chevron-unten ${styles.Icon} `} />}
                      </IconButton>
                    </div>
                    <Menu
                      id="menu-automatic-translations"
                      anchorEl={anchorEl}
                      keepMounted
                      open={Boolean(anchorEl)}
                      onClose={closeMenu}
                      slotProps={{ paper: { sx: { width: 170 } } }}
                    >
                      {([{ label: 'translations.autoTranslationSelection.all', value: 'all' }, { label: 'translations.autoTranslationSelection.empty', value: 'empty' }]).map((option) => (
                        <MenuItem
                          key={option.value}
                          onClick={() => selectTranslation(option.value)}
                        >
                          {t(option.label)}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                </div>
              </div>
              {translations?.modules && Object.keys(modulesToList(translations, campaignInfo)).map((tab, index) => (
                <TranslationsAccordion
                  key={tab}
                  disabled={checkDisabled}
                  tab={tab}
                  index={index}
                  isExpandedAll={isExpandedAll}
                  onExpand={() => (opens?.includes(tab)
                    ? setOpens(opens.filter((item) => item !== tab))
                    : setOpens([...opens, tab]))}
                  onInputChange={tab === 'AFFECTED_VEHICLES' ? handleVehiclesTranslationsChange : handleTranslationChange}
                  translations={translations.modules[tab]}
                  translationLang={translationLang}
                  defaultLanguage={defaultLanguage}
                  translitonsValidations={translitonsValidations}
                  validationActive={validations?.active?.all}
                />
              ))}
            </div>
          )}
      </AccordionPanel>

      {isOpenPopupAutoTrans && (
        <ConfirmDialog
          close={() => setOpenPopupAutoTrans(false)}
          confirm={() => handleAcceptAutoTrans(false)}
          title={t('dialog.automaticTranslation.title')}
          message={t('dialog.automaticTranslation.message')}
          closeBtnText={t('dialog.close')}
          confirmBtnText={t('dialog.accept')}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  translations: getCampaingTranslations(state),
  translitonsValidations: getTranslationValidations(state),
  defaultLanguage: getCampaignInfo(state)?.defaultLanguage,
  campaignInfo: getCampaignInfo(state),
  validations: getCampaignValidations(state),
  originalValues: getOriginalValues(state),
  pendingVolumes: getArePendingVolumes(state),
});

const actionCreators = {
  setCampaignTrans: setCampaignTranslations,
  setTransValidations: setTranslationValidations,
  setTransData: setTranslations,
  setHelpArea: setAreaHelp,
};

export default connect(mapStateToProps, actionCreators)(Translations);
